<template>
  <MainLayout>

    <!-- -- Hero ----------------------------------------------------------------- -->
    <section class="relative bg-gradient-hero py-7 lg:py-10 overflow-hidden">
      <div class="absolute inset-0 hex-pattern opacity-100 pointer-events-none"></div>
      <div class="absolute right-1/4 top-0 w-[400px] h-[400px] bg-secondary/10 rounded-full blur-[100px] pointer-events-none"></div>
      <!-- DNA helix decoration -->
      <svg class="absolute right-0 top-0 h-full w-36 opacity-10 hidden xl:block pointer-events-none" viewBox="0 0 100 400" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
        <path d="M20 0 Q80 50 20 100 Q-40 150 20 200 Q80 250 20 300 Q-40 350 20 400" stroke="#9AB4C8" stroke-width="2"/>
        <path d="M80 0 Q20 50 80 100 Q140 150 80 200 Q20 250 80 300 Q140 350 80 400" stroke="#9AB4C8" stroke-width="2"/>
        <line x1="20" y1="25"  x2="80" y2="25"  stroke="#9AB4C8" stroke-width="1"/>
        <line x1="20" y1="75"  x2="80" y2="75"  stroke="#9AB4C8" stroke-width="1"/>
        <line x1="20" y1="125" x2="80" y2="125" stroke="#9AB4C8" stroke-width="1"/>
        <line x1="20" y1="175" x2="80" y2="175" stroke="#9AB4C8" stroke-width="1"/>
        <line x1="20" y1="225" x2="80" y2="225" stroke="#9AB4C8" stroke-width="1"/>
        <line x1="20" y1="275" x2="80" y2="275" stroke="#9AB4C8" stroke-width="1"/>
        <line x1="20" y1="325" x2="80" y2="325" stroke="#9AB4C8" stroke-width="1"/>
        <line x1="20" y1="375" x2="80" y2="375" stroke="#9AB4C8" stroke-width="1"/>
      </svg>

      <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div class="flex justify-center mb-4 animate-fade-up" style="animation-delay:0.1s">
          <div class="flex items-center gap-3">
            <span class="w-8 h-0.5 bg-secondary rounded-full"></span>
            <span class="text-secondary text-xs font-bold uppercase tracking-[0.2em]">Who We Are</span>
            <span class="w-8 h-0.5 bg-secondary rounded-full"></span>
          </div>
        </div>
        <h1 class="text-5xl sm:text-6xl font-black text-white mb-4 animate-fade-up" style="animation-delay:0.2s">
          About <span class="text-secondary">Orel</span>
        </h1>
        <p class="text-white/60 text-base max-w-xl mx-auto mb-6 animate-fade-up" style="animation-delay:0.3s">
          Founded in 2022 by biomedical engineers - pioneering healthcare excellence across Uganda one breakthrough at a time.
        </p>
        <nav class="flex items-center justify-center gap-2 text-sm text-white/50 animate-fade-up" style="animation-delay:0.4s">
          <RouterLink to="/" class="hover:text-secondary transition-colors duration-200">Home</RouterLink>
          <i class="fa-solid fa-chevron-right text-xs text-white/30"></i>
          <span class="text-white/80">About Orel</span>
        </nav>
      </div>
    </section>

    <!-- -- About Story ---------------------------------------------------------- -->
    <section class="py-20 lg:py-28 bg-white overflow-hidden">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid lg:grid-cols-2 gap-12 xl:gap-20 items-center">

          <!-- Left: Image -->
          <div class="relative" data-aos="fade-right">
            <div class="absolute -top-8 -left-8 w-64 h-64 bg-secondary/10 rounded-3xl -z-10"></div>
            <div class="absolute -bottom-8 -right-8 w-36 h-36 bg-primary/8 rounded-2xl -z-10"></div>
            <div class="absolute top-12 left-12 w-32 h-32 dot-pattern opacity-40 -z-10"></div>
            <div class="relative rounded-3xl overflow-hidden shadow-card-lg">
              <img
                :src="aboutImg"
                alt="Orel Medical Supplies team at work"
                class="w-full h-[480px] object-cover"
                loading="lazy"
              />
              <div class="absolute inset-0 bg-gradient-to-t from-primary-darkest/40 to-transparent pointer-events-none"></div>
            </div>
            <!-- Floating Year Badge -->
            <div class="absolute -bottom-6 -right-4 bg-gradient-primary text-white rounded-2xl p-5 shadow-card-lg animate-float">
              <p class="text-3xl font-black leading-none">2022</p>
              <p class="text-white/70 text-xs mt-1">Year Founded</p>
            </div>
            <!-- Floating expertise badge -->
            <div class="absolute -top-4 -left-4 bg-white border border-primary/10 text-primary rounded-2xl px-4 py-3 shadow-card animate-float" style="animation-delay:1s">
              <p class="text-lg font-black leading-none">12+</p>
              <p class="text-gray-400 text-xs mt-0.5">Years Expertise</p>
            </div>
          </div>

          <!-- Right: Content -->
          <div data-aos="fade-left" data-aos-delay="150">
            <div class="section-label">About Orel Supplies</div>
            <h2 class="section-title mb-6">
              We Execute Our Ideas From The Start to Finish
            </h2>
            <p class="text-gray-500 leading-relaxed mb-5 text-justify">
              At Orel Supplies, we're more than just a company; we're pioneers envisioning the future of healthcare in Uganda. Established in 2022 by a dedicated team of biomedical engineers, our mission is to equip healthcare providers with the quality resources and expertise necessary to provide exceptional patient care.
            </p>
            <p class="text-gray-500 leading-relaxed mb-8 text-justify">
              Driven by our commitment to create impact, we merge state-of-the-art technology with tailored services, shaping the healthcare landscape through continuous innovation. At Orel, we're dedicated to transforming healthcare - one breakthrough at a time.
            </p>

            <!-- Mission and Vision -->
            <div class="space-y-4 mb-8">
              <div class="flex items-start gap-4 p-5 bg-primary/5 rounded-2xl border border-primary/10">
                <div class="w-10 h-10 bg-primary rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5 text-white">
                    <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>
                  </svg>
                </div>
                <div>
                  <h4 class="font-bold text-primary text-sm mb-1.5">Our Vision</h4>
                  <p class="text-gray-600 text-sm leading-relaxed">To provide exceptional critical devices and services to enhance lives across Uganda and the East African region.</p>
                </div>
              </div>
              <div class="flex items-start gap-4 p-5 bg-secondary/10 rounded-2xl border border-secondary/20">
                <div class="w-10 h-10 bg-secondary rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5 text-white">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                  </svg>
                </div>
                <div>
                  <h4 class="font-bold text-secondary-dark text-sm mb-1.5">Our Mission</h4>
                  <p class="text-gray-600 text-sm leading-relaxed">To revolutionize healthcare quality by delivering premium biomedical equipment, expert consultancy, and end-to-end service support.</p>
                </div>
              </div>
            </div>

            <RouterLink to="/contact-us" class="btn-primary">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
              Work With Us
            </RouterLink>
          </div>

        </div>
      </div>
    </section>

    <!-- -- Core Values ---------------------------------------------------------- -->
    <section class="py-20 lg:py-28 bg-[#f7f5fb] overflow-hidden">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <div class="text-center max-w-2xl mx-auto mb-14" data-aos="fade-up">
          <div class="flex justify-center">
            <div class="section-label">What Drives Us</div>
          </div>
          <h2 class="section-title">Our Core Values</h2>
          <p class="section-subtitle">The principles that guide every decision, partnership, and product we deliver.</p>
        </div>

        <div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div
            v-for="val in coreValues"
            :key="val.title"
            class="bg-white rounded-3xl p-8 shadow-card hover:shadow-card-lg hover:-translate-y-1 transition-all duration-300 text-center group"
            data-aos="fade-up"
            :data-aos-delay="val.delay"
          >
            <div class="w-16 h-16 rounded-2xl bg-primary/8 group-hover:bg-gradient-primary flex items-center justify-center mx-auto mb-5 transition-all duration-300">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-7 h-7 text-primary group-hover:text-white transition-colors duration-300" v-html="val.svgPath"></svg>
            </div>
            <h3 class="font-black text-gray-900 text-lg mb-3">{{ val.title }}</h3>
            <p class="text-gray-500 text-sm leading-relaxed">{{ val.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- -- Business Process ---------------------------------------------------- -->
    <section class="py-20 lg:py-28 bg-white overflow-hidden">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <div class="text-center max-w-2xl mx-auto mb-14" data-aos="fade-up">
          <div class="flex justify-center">
            <div class="section-label">Business Process</div>
          </div>
          <h2 class="section-title">Empowering Healthcare Excellence: Our Proven Process</h2>
        </div>

        <div class="grid lg:grid-cols-3 gap-6 lg:gap-8">
          <div
            v-for="process in processes"
            :key="process.number"
            class="relative bg-white rounded-3xl p-8 shadow-card hover:shadow-card-lg hover:-translate-y-1 transition-all duration-300 overflow-hidden border border-gray-100"
            data-aos="fade-up"
            :data-aos-delay="process.delay"
          >
            <!-- Large number watermark -->
            <span class="absolute top-4 right-6 text-[80px] font-black text-primary/5 leading-none select-none">
              {{ process.number }}
            </span>

            <!-- Icon -->
            <div class="w-14 h-14 bg-gradient-primary rounded-2xl flex items-center justify-center mb-5 shadow-md">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-7 h-7 text-white" v-html="process.svgPath"></svg>
            </div>

            <!-- Step badge -->
            <span class="inline-block text-xs font-black text-primary bg-primary/10 px-2.5 py-1 rounded-full mb-3 uppercase tracking-widest">
              Step {{ process.number }}
            </span>

            <h3 class="font-black text-gray-900 text-lg mb-5 leading-snug">{{ process.title }}</h3>

            <!-- What We Do -->
            <div class="mb-4">
              <div class="flex items-center gap-2 mb-2">
                <div class="w-1 h-4 bg-primary rounded-full"></div>
                <span class="text-xs font-bold text-primary uppercase tracking-wider">What We Do</span>
              </div>
              <p class="text-gray-500 text-sm leading-relaxed pl-3">{{ process.whatWeDo }}</p>
            </div>

            <!-- Why Choose Us -->
            <div>
              <div class="flex items-center gap-2 mb-2">
                <div class="w-1 h-4 bg-secondary rounded-full"></div>
                <span class="text-xs font-bold text-secondary-dark uppercase tracking-wider">Why Choose Us</span>
              </div>
              <p class="text-gray-500 text-sm leading-relaxed pl-3">{{ process.whyUs }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- -- Our Team ------------------------------------------------------------ -->
    <section class="py-20 lg:py-28 bg-[#f7f5fb] overflow-hidden">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <div class="text-center max-w-2xl mx-auto mb-14" data-aos="fade-up">
          <div class="flex justify-center">
            <div class="section-label">Our People</div>
          </div>
          <h2 class="section-title">Meet the Team Behind Orel</h2>
          <p class="section-subtitle">
            A dedicated group of biomedical engineers and healthcare professionals committed to transforming healthcare delivery across Uganda.
          </p>
        </div>

        <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
          <div
            v-for="(member, index) in team"
            :key="index"
            class="group relative rounded-2xl overflow-hidden shadow-card hover:shadow-card-lg transition-all duration-300 hover:-translate-y-1 bg-white"
            data-aos="fade-up"
            :data-aos-delay="index * 80"
          >
            <!-- Headshot placeholder -->
            <div class="aspect-[3/4] bg-gradient-to-b from-primary/10 to-primary/5 flex flex-col items-center justify-center relative overflow-hidden">
              <!-- Subtle dot pattern -->
              <div class="absolute inset-0 dot-pattern opacity-30 pointer-events-none"></div>
              <!-- Avatar silhouette -->
              <div class="relative z-10 flex flex-col items-center gap-3">
                <div class="w-20 h-20 rounded-full bg-primary/15 border-2 border-primary/20 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" class="w-10 h-10 text-primary/50" fill="currentColor">
                    <circle cx="12" cy="8" r="4"/>
                    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
                  </svg>
                </div>
                <span class="text-[10px] font-semibold text-primary/50 uppercase tracking-widest text-center leading-tight px-2">
                  Photo<br/>Coming Soon
                </span>
              </div>
            </div>
            <!-- Bottom label -->
            <div class="bg-white px-4 py-3 border-t border-gray-100">
              <p class="text-gray-800 font-bold text-xs leading-tight truncate">{{ member.role }}</p>
              <p class="text-primary text-xs mt-0.5">{{ member.dept }}</p>
            </div>
          </div>
        </div>

        <!-- Team CTA strip -->
        <div class="mt-12 bg-white rounded-3xl p-8 flex flex-col sm:flex-row items-center justify-between gap-6 shadow-card" data-aos="fade-up">
          <div class="flex items-center gap-4">
            <!-- Stacked avatars -->
            <div class="flex -space-x-3">
              <div v-for="i in 4" :key="i" class="w-10 h-10 rounded-full bg-gradient-primary border-2 border-white flex items-center justify-center">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4 text-white">
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
              </div>
            </div>
            <div>
              <p class="font-black text-gray-900 text-sm">10+ Team Members</p>
              <p class="text-gray-400 text-xs">Biomedical engineers and healthcare specialists</p>
            </div>
          </div>
          <RouterLink to="/contact-us" class="btn-primary flex-shrink-0">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
            </svg>
            Join Our Team
          </RouterLink>
        </div>
      </div>
    </section>

    <!-- -- Brands -------------------------------------------------------------- -->
    <section class="py-14 bg-gradient-primary relative overflow-hidden">
      <div class="absolute inset-0 dot-pattern opacity-15 pointer-events-none"></div>
      <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-10">
          <h3 class="text-white/80 text-sm font-bold uppercase tracking-[0.2em]">Our Trusted Brands</h3>
        </div>
        <div class="flex flex-wrap items-center justify-center gap-8 sm:gap-12 lg:gap-16">
          <img
            v-for="i in 5"
            :key="i"
            :src="'/assets/images/brand/' + i + '.png'"
            :alt="'Brand partner ' + i"
            class="h-10 sm:h-12 w-auto object-contain opacity-60 hover:opacity-100 transition-opacity duration-300 filter brightness-0 invert"
            loading="lazy"
          />
        </div>
      </div>
    </section>

    <!-- -- Stats --------------------------------------------------------------- -->
    <section class="py-16 bg-white border-t border-gray-100">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-0 divide-y-2 lg:divide-y-0 lg:divide-x divide-gray-100">
          <div
            v-for="stat in aboutStats"
            :key="stat.label"
            class="flex flex-col items-center py-10 sm:py-6 text-center group"
            data-aos="fade-up"
            :data-aos-delay="stat.delay"
          >
            <div class="w-16 h-16 rounded-2xl bg-primary/8 group-hover:bg-gradient-primary flex items-center justify-center mb-4 transition-all duration-300">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-7 h-7 text-primary group-hover:text-white transition-colors duration-300" v-html="stat.svgPath"></svg>
            </div>
            <p class="text-5xl font-black text-gray-900 leading-none mb-1">{{ stat.value }}</p>
            <p class="text-gray-400 text-sm uppercase tracking-wider">{{ stat.label }}</p>
          </div>
        </div>
      </div>
    </section>

  </MainLayout>
</template>

<script setup>
import MainLayout from './Layout/MainLayout.vue'

const aboutImg = require('../assets/orel-imgs/about4.jpg')

const coreValues = [
  {
    title: 'Integrity',
    desc:  'We operate with transparency and honesty in every partnership, procurement, and professional engagement.',
    svgPath: '<path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>',
    delay: 0,
  },
  {
    title: 'Excellence',
    desc:  'We are committed to delivering the highest quality biomedical products and services - no compromises.',
    svgPath: '<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>',
    delay: 100,
  },
  {
    title: 'Innovation',
    desc:  'We continuously explore emerging technologies and methodologies to advance healthcare outcomes in Uganda.',
    svgPath: '<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>',
    delay: 200,
  },
  {
    title: 'Patient Focus',
    desc:  'Every product we supply and every service we render is measured by its impact on patient well-being.',
    svgPath: '<path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>',
    delay: 300,
  },
]

const processes = [
  {
    number:    '01',
    svgPath:   '<rect x="1" y="3" width="15" height="13" rx="1"/><polygon points="16 8 20 8 23 11 23 16 16 16"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>',
    title:     'Efficient Supply Chain Management',
    whatWeDo:  'We source top-quality medical supplies directly from certified, internationally recognized manufacturers - ensuring reliability and cost-effectiveness at every stage.',
    whyUs:     'Our streamlined procurement model guarantees timely delivery of essential supplies, freeing healthcare facilities to focus entirely on patient care without stock availability concerns.',
    delay:     0,
  },
  {
    number:    '02',
    svgPath:   '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/>',
    title:     'Stringent Quality Assurance and Compliance',
    whatWeDo:  'We implement rigorous quality control at every step - pre-shipment inspection, documentation review, and post-delivery verification - to ensure every product meets WHO and national regulatory standards.',
    whyUs:     'With our unwavering commitment to compliance, healthcare providers can trust in the absolute safety and reliability of every item bearing the Orel guarantee.',
    delay:     150,
  },
  {
    number:    '03',
    svgPath:   '<path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>',
    title:     'Personalized Customer Relationship Management',
    whatWeDo:  'We provide tailored customer support from initial inquiry through procurement, delivery, installation, and post-sales assistance - a seamless, fully managed experience.',
    whyUs:     'Our dedicated team offers expert advice, rapid order processing, and proactive support - anticipating your needs before they become challenges.',
    delay:     300,
  },
]

const team = [
  { role: 'Founding Director',         dept: 'Leadership'         },
  { role: 'Lead Biomedical Engineer',  dept: 'Engineering'        },
  { role: 'Supply Chain Manager',      dept: 'Operations'         },
  { role: 'Business Development Lead', dept: 'Commercial'         },
  { role: 'Field Technical Officer',   dept: 'Technical Services' },
]

const aboutStats = [
  {
    value:   '10+',
    label:   'Team Members',
    svgPath: '<path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>',
    delay:   0,
  },
  {
    value:   '1k+',
    label:   'Products',
    svgPath: '<path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>',
    delay:   100,
  },
  {
    value:   '200+',
    label:   'Customer Reviews',
    svgPath: '<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>',
    delay:   200,
  },
  {
    value:   '12+',
    label:   'Years Expertise',
    svgPath: '<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>',
    delay:   300,
  },
]
</script>
